import { useState, useEffect, useRef, useCallback } from 'react'
import Modal from './Modal'
import Boton from './Boton'
import api from '../api/client'
import { fotoArticuloUrl } from '../api/notasVenta'
import { fmtMonto } from '../utils/fecha'
import { colors, text, space, radius, sx } from '../design/tokens'

// Mismo formato de moneda que toda la app (fmtMonto), no un locale propio.
const fmt = n => '$' + fmtMonto(parseFloat(n) || 0)

/**
 * EL selector de artículos de LGR — único en toda la app (regla de Martin,
 * 8 sep 2026: "cada vez que haya un menú con carga de producto tiene que llamar
 * a un único modal; si este cambia, que cambien todos"). Aprobado el 2 sep
 * sobre el prototipo funcional: rubro · subrubro · proveedor a la vista,
 * filtros combinables, selección múltiple rápida (click, Shift+click de rango,
 * tilde de cabecera para todo lo filtrado) y panel de seleccionados con
 * cantidad. Reemplazó a SelectorArticuloMultiModal y SelectorArticuloModal.
 *
 * Modos:
 *  - modo="multiple" (default): onConfirm([{articulo, cantidad}]).
 *  - modo="simple": un solo artículo; el click en la fila llama onSelect(articulo)
 *    y cierra. Sin panel de seleccionados.
 * precios: {articulo_id: precio} de la lista en uso; sin él no se muestra la
 * columna Precio (propuestas, remitos, composición del club no la necesitan).
 * filtro: callback opcional para acotar las filas del lado del cliente
 * (ej. solo artículos con código Presea).
 * preseleccion: [{articulo, cantidad}] para abrir el panel de Seleccionados
 * ya cargado (ej. lo que interpretó la IA de un mail pegado) — el vendedor
 * ajusta/quita antes de confirmar, nunca se agrega sin su revisión.
 * avisos: array de strings — banner arriba de la tabla (ej. renglones del
 * pedido que la IA no pudo matchear, para buscarlos acá a mano).
 * conFoto: columna con la miniatura de la foto del catálogo (grande al pasar
 * el mouse) y el EAN. soloConEan: el filtro EAN arranca en "Con EAN" (se puede cambiar).
 * sinCantidad: el panel de seleccionados no pide cantidad. textoConfirmar:
 * rótulo del botón (default "➕ Agregar"). Los usa STOCK → Fotos pendientes
 * para elegir productos y buscar sus datos en la web (Martin, 29 sep 2026).
 */
export default function SelectorArticulosModal({
  onConfirm, onSelect, onClose, precios, clienteId = null, lista = null,
  modo = 'multiple', filtro = null, titulo = null, preseleccion = null, avisos = null,
  // Con `habitualesDe` (id de cliente) el listado se acota a lo que ESE cliente
  // compra: su historia real de Presea (ventas_lineas, 24 meses) más sus NV de
  // LGR, ordenado por veces comprado. El resto del modal funciona igual — los
  // filtros y la búsqueda siguen aplicando sobre ese universo más chico.
  habitualesDe = null,
  conFoto = false, soloConEan = false, sinCantidad = false, textoConfirmar = null,
}) {
  const simple = modo === 'simple'
  const conPrecio = precios != null
  // El filtro suele venir como arrow inline (cambia en cada render): va por
  // ref para no re-disparar la consulta al backend en cada pintado.
  const filtroRef = useRef(filtro)
  useEffect(() => { filtroRef.current = filtro })
  const [q, setQ] = useState('')
  const [fRubro, setFRubro] = useState('')
  const [fSub, setFSub] = useState('')
  const [fProv, setFProv] = useState('')
  // Stock y disponibilidad (Martin, 30 sep 2026) — mismo criterio que las
  // columnas de Artículos: disponible = se muestra en la tienda; stock = se puede comprar.
  const [fDisp, setFDisp] = useState('')
  const [fStock, setFStock] = useState('')
  // soloConEan (Fotos pendientes) arranca en "Con EAN" pero se puede cambiar (Martin, 30 sep 2026).
  const [fEan, setFEan] = useState(soloConEan ? 'si' : '')
  const [fImagen, setFImagen] = useState('')
  // Con `lista`, el listado trae SOLO lo que tiene precio en ella (sug #104).
  // Cargar un artículo fuera de la lista es una acción explícita: este tilde.
  const [fueraDeLista, setFueraDeLista] = useState(false)
  const [rubros, setRubros] = useState([])
  const [subrubros, setSubrubros] = useState([])
  const [provs, setProvs] = useState([])
  const [filas, setFilas] = useState([])
  const [total, setTotal] = useState(0)
  const [cargando, setCargando] = useState(false)
  // Map id -> {articulo, cantidad}
  const [sel, setSel] = useState(() => {
    if (!preseleccion?.length) return new Map()
    const m = new Map()
    preseleccion.forEach(({ articulo, cantidad }) => { if (articulo?.id) m.set(articulo.id, { articulo, cantidad: cantidad || 1 }) })
    return m
  })
  const [previa, setPrevia] = useState(null) // {src, x, y}: foto grande al pasar el mouse
  const lastClick = useRef(-1)
  const timer = useRef(null)

  const paramsFiltros = useCallback((sinCampo = null) => {
    // Lo que se puede vender hoy, primero (sug #134).
    const p = { activos: true, per_page: 500, disponibles_primero: 1 }
    if (q.trim()) p.q = q.trim()
    if (lista && !fueraDeLista) p.lista = lista
    if (clienteId) p.cliente_id = clienteId
    if (habitualesDe) p.habituales_cliente = habitualesDe
    if (fRubro && sinCampo !== 'rubro') p['f[rubro]'] = fRubro
    if (fSub && sinCampo !== 'subrubro') p['f[subrubro]'] = fSub
    if (fProv && sinCampo !== 'nomprov') p['f[nomprov]'] = fProv
    if (fDisp) p.disponible = fDisp
    if (fStock) p.stock = fStock
    if (fEan) p.ean = fEan
    if (fImagen) p.imagen = fImagen
    return p
  }, [q, lista, fueraDeLista, clienteId, habitualesDe, soloConEan, fRubro, fSub, fProv, fDisp, fStock, fEan, fImagen])

  // Listado (debounced sobre el texto, inmediato sobre los selects).
  useEffect(() => {
    clearTimeout(timer.current)
    timer.current = setTimeout(async () => {
      setCargando(true)
      try {
        const { data } = await api.get('/articulos', { params: paramsFiltros() })
        const lista = filtroRef.current ? (data.data || []).filter(filtroRef.current) : (data.data || [])
        setFilas(lista)
        setTotal(data.total ?? lista.length)
      } catch { setFilas([]); setTotal(0) }
      setCargando(false)
    }, q.trim() ? 300 : 0)
    return () => clearTimeout(timer.current)
  }, [paramsFiltros, q])

  // Facetas DEPENDIENTES: cada selector se acota según los otros filtros.
  // Debounced igual que el listado: sin esto eran 3 consultas por CADA tecla.
  const timerFacetas = useRef(null)
  useEffect(() => {
    clearTimeout(timerFacetas.current)
    timerFacetas.current = setTimeout(() => {
      const traer = (campo, setter, sinCampo) =>
        api.get('/articulos/facetas', { params: { campo, ...paramsFiltros(sinCampo) } })
          .then(({ data }) => setter(Array.isArray(data) ? data : []))
          .catch(() => setter([]))
      traer('rubro', setRubros, 'rubro')
      traer('subrubro', setSubrubros, 'subrubro')
      traer('nomprov', setProvs, 'nomprov')
    }, q.trim() ? 350 : 0)
    return () => clearTimeout(timerFacetas.current)
  }, [paramsFiltros, q])

  const toggle = (a) => {
    setSel(prev => {
      const m = new Map(prev)
      if (m.has(a.id)) m.delete(a.id)
      else m.set(a.id, { articulo: a, cantidad: 1 })
      return m
    })
  }

  const clickFila = (ix, e) => {
    const a = filas[ix]
    if (simple) {
      onSelect?.(a)
      onClose()
      return
    }
    if (e.shiftKey && lastClick.current >= 0) {
      const [d, h] = [Math.min(lastClick.current, ix), Math.max(lastClick.current, ix)]
      const marcar = !sel.has(a.id)
      setSel(prev => {
        const m = new Map(prev)
        for (let k = d; k <= h; k++) {
          const art = filas[k]
          if (marcar) { if (!m.has(art.id)) m.set(art.id, { articulo: art, cantidad: 1 }) }
          else m.delete(art.id)
        }
        return m
      })
    } else {
      toggle(a)
    }
    lastClick.current = ix
  }

  const todosFiltrados = filas.length > 0 && filas.every(a => sel.has(a.id))
  const algunoFiltrado = filas.some(a => sel.has(a.id))

  const toggleTodos = () => {
    setSel(prev => {
      const m = new Map(prev)
      if (todosFiltrados) filas.forEach(a => m.delete(a.id))
      else filas.forEach(a => { if (!m.has(a.id)) m.set(a.id, { articulo: a, cantidad: 1 }) })
      return m
    })
  }

  const setCantidad = (id, v) => {
    setSel(prev => {
      const m = new Map(prev)
      const it = m.get(id)
      if (it) m.set(id, { ...it, cantidad: v })
      return m
    })
  }

  const limpiarFiltros = () => { setQ(''); setFRubro(''); setFSub(''); setFProv(''); setFDisp(''); setFStock(''); setFEan(soloConEan ? 'si' : ''); setFImagen('') }

  // Unidades marcadas (Martin, 5 oct 2026: "que el vendedor vaya sabiendo").
  const unidadesSel = [...sel.values()].reduce((a, x) => a + (parseInt(x.cantidad) || 0), 0)
  const resumenSel = `${sel.size} ${sel.size === 1 ? 'artículo' : 'artículos'}`
    + (sinCantidad ? '' : ` · ${unidadesSel} ${unidadesSel === 1 ? 'unidad' : 'unidades'}`)
  const totalSel = [...sel.values()].reduce((acc, { articulo, cantidad }) => {
    const p = precios ? precios[articulo.id] : null
    return acc + (p != null ? p * (parseInt(cantidad) || 0) : 0)
  }, 0)
  const haySinPrecio = [...sel.values()].some(({ articulo }) => !precios || precios[articulo.id] == null)

  const confirmar = () => {
    if (sel.size === 0) return
    onConfirm([...sel.values()].map(({ articulo, cantidad }) => ({ articulo, cantidad: parseInt(cantidad) || 1 })))
    onClose()
  }

  // Celular (LGR móvil /m, Martin 30 sep 2026): el MISMO modal —mismos
  // filtros, misma consulta, misma selección múltiple— en tarjetas apiladas
  // para el pulgar en vez de la tabla de 9 columnas.
  const compacto = useEsAngosto()
  const [verFiltros, setVerFiltros] = useState(false)
  if (compacto) {
    const nFiltros = [fRubro, fSub, fProv, fDisp, fStock, fImagen, soloConEan ? '' : fEan].filter(Boolean).length
    return (
      <Modal title={titulo || (simple ? 'Buscar artículo' : 'Buscar artículos')} onClose={onClose} maxWidth="100%">
        {avisos?.length > 0 && (
          <div style={st.avisos}>
            <b>No se encontró un artículo claro para:</b> buscalos acá a mano.
            <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
              {avisos.map((a, i) => <li key={i}>{a}</li>)}
            </ul>
          </div>
        )}
        <div style={{ display: 'flex', gap: space.sm }}>
          <input
            style={stc.input}
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Buscar artículo…"
            type="search"
          />
          <button type="button" style={{ ...stc.btnFiltros, ...(verFiltros || nFiltros ? { borderColor: colors.primary, color: colors.link } : {}) }} onClick={() => setVerFiltros(v => !v)}>
            Filtros{nFiltros ? ` · ${nFiltros}` : ''}
          </button>
        </div>
        {verFiltros && (
          <div style={stc.filtros}>
            <select style={stc.select} value={fRubro} onChange={e => { setFRubro(e.target.value); setFSub('') }}>
              <option value="">Rubro — todos</option>
              {rubros.map(r => <option key={r}>{r}</option>)}
            </select>
            <select style={stc.select} value={fSub} onChange={e => setFSub(e.target.value)}>
              <option value="">Subrubro — todos</option>
              {subrubros.map(r => <option key={r}>{r}</option>)}
            </select>
            <select style={stc.select} value={fProv} onChange={e => setFProv(e.target.value)}>
              <option value="">Proveedor — todos</option>
              {provs.map(r => <option key={r}>{r}</option>)}
            </select>
            <select style={stc.select} value={fStock} onChange={e => setFStock(e.target.value)}>
              <option value="">Stock — todos</option>
              <option value="si">En stock</option>
              <option value="no">Sin stock</option>
            </select>
            <select style={stc.select} value={fDisp} onChange={e => setFDisp(e.target.value)}>
              <option value="">Disponible — todos</option>
              <option value="si">Visible en la web</option>
              <option value="no">No visible</option>
            </select>
            <select style={stc.select} value={fEan} onChange={e => setFEan(e.target.value)}>
              <option value="">EAN — todos</option>
              <option value="si">Con EAN</option>
              <option value="no">Sin EAN</option>
            </select>
            <select style={stc.select} value={fImagen} onChange={e => setFImagen(e.target.value)}>
              <option value="">Imagen — todos</option>
              <option value="si">Con imagen</option>
              <option value="no">Sin imagen</option>
            </select>
            {lista && (
              <label style={{ display: 'flex', alignItems: 'center', gap: space.xs, fontSize: text.sec, color: colors.textSec }}>
                <input type="checkbox" checked={fueraDeLista} onChange={e => setFueraDeLista(e.target.checked)} />
                Incluir artículos fuera de {lista}
              </label>
            )}
            <button type="button" style={stc.btnFiltros} onClick={limpiarFiltros}>Limpiar</button>
          </div>
        )}
        <div style={{ ...st.meta, margin: `${space.sm}px 2px` }}>
          <span>
            <b style={{ color: colors.text }}>{filas.length}</b>
            {total > filas.length ? ` de ${total} — afiná la búsqueda` : ' artículos'}
            {cargando ? ' · buscando…' : ''}
          </span>
          {!simple && filas.length > 0 && (
            <button type="button" style={stc.linkBtn} onClick={toggleTodos}>{todosFiltrados ? 'Destildar todo' : 'Tildar todo'}</button>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {filas.map((a, ix) => {
            const on = sel.has(a.id)
            const p = precios ? precios[a.id] : null
            return (
              <div key={a.id} onClick={e => clickFila(ix, e)}
                style={{ ...stc.card, ...(on ? { borderColor: colors.primary, background: colors.linkBg } : {}) }}>
                {!simple && <span style={{ ...stc.check, ...(on ? stc.checkOn : {}) }}>{on ? '✓' : ''}</span>}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={stc.desc}>{a.descripcion}</div>
                  <div style={stc.sub}>
                    <span style={sx.mono}>{a.codigo_alfa}</span>
                    {a.rubro ? ` · ${a.rubro}` : ''}
                  </div>
                  {conPrecio && (
                    <div style={{ marginTop: 2 }}>
                      {p != null ? <b style={{ ...sx.mono, fontSize: text.sec }}>{fmt(p)}</b> : <span style={st.sinPrecio}>elegir precio</span>}
                    </div>
                  )}
                </div>
                {on && !simple && !sinCantidad && (
                  <input
                    style={stc.cant}
                    type="text" inputMode="numeric"
                    value={sel.get(a.id).cantidad}
                    onClick={e => e.stopPropagation()}
                    onChange={e => setCantidad(a.id, e.target.value.replace(/\D/g, ''))}
                    aria-label="Cantidad"
                  />
                )}
              </div>
            )
          })}
          {filas.length === 0 && !cargando && (
            <div style={st.vacio}>Sin resultados con estos filtros.</div>
          )}
        </div>
        {!simple && (
          <div style={stc.barra}>
            {sel.size > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: text.sec, marginBottom: space.sm }}>
                <span>{resumenSel}{conPrecio ? ' · total estimado' : ''}</span>
                {conPrecio && <b style={sx.mono}>{fmt(totalSel)}{haySinPrecio ? ' *' : ''}</b>}
              </div>
            )}
            <div style={{ display: 'flex', gap: space.sm }}>
              <button type="button" style={stc.btnCancelar} onClick={onClose}>Cancelar</button>
              <button type="button" style={{ ...stc.btnAgregar, opacity: sel.size ? 1 : 0.5 }} disabled={sel.size === 0} onClick={confirmar}>
                {textoConfirmar || '➕ Agregar'}{sel.size ? ` (${sel.size})` : ''}
              </button>
            </div>
          </div>
        )}
      </Modal>
    )
  }

  return (
    <Modal title={titulo || (simple ? 'Buscar artículo' : 'Buscar artículos — selección múltiple')} onClose={onClose} maxWidth={simple ? '1060px' : '1320px'}>
      <div style={simple ? st.layoutSimple : st.layout}>
        <div style={{ minWidth: 0 }}>
          {avisos?.length > 0 && (
            <div style={st.avisos}>
              <b>No se encontró un artículo claro para:</b> buscalos acá a mano.
              <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                {avisos.map((a, i) => <li key={i}>{a}</li>)}
              </ul>
            </div>
          )}
          <div style={st.filtros}>
            <input
              autoFocus
              style={st.input}
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Buscar… (varios términos combinan)"
            />
            <select style={st.input} value={fRubro} onChange={e => { setFRubro(e.target.value); setFSub('') }}>
              <option value="">Rubro — todos</option>
              {rubros.map(r => <option key={r}>{r}</option>)}
            </select>
            <select style={st.input} value={fSub} onChange={e => setFSub(e.target.value)}>
              <option value="">Subrubro — todos</option>
              {subrubros.map(r => <option key={r}>{r}</option>)}
            </select>
            <select style={st.input} value={fProv} onChange={e => setFProv(e.target.value)}>
              <option value="">Proveedor — todos</option>
              {provs.map(r => <option key={r}>{r}</option>)}
            </select>
            <Boton variante="ghost" sm type="button" onClick={limpiarFiltros}>Limpiar</Boton>
          </div>
          <div style={st.filtros2}>
            <select style={st.input} value={fStock} onChange={e => setFStock(e.target.value)} title="Stock = se puede comprar">
              <option value="">Stock — todos</option>
              <option value="si">En stock</option>
              <option value="no">Sin stock</option>
            </select>
            <select style={st.input} value={fDisp} onChange={e => setFDisp(e.target.value)} title="Disponible = se muestra en la tienda">
              <option value="">Disponible — todos</option>
              <option value="si">Visible en la web</option>
              <option value="no">No visible</option>
            </select>
            <select style={st.input} value={fEan} onChange={e => setFEan(e.target.value)} title="Código de barras cargado">
              <option value="">EAN — todos</option>
              <option value="si">Con EAN</option>
              <option value="no">Sin EAN</option>
            </select>
            <select style={st.input} value={fImagen} onChange={e => setFImagen(e.target.value)} title="Foto del producto cargada">
              <option value="">Imagen — todos</option>
              <option value="si">Con imagen</option>
              <option value="no">Sin imagen</option>
            </select>
            {lista && (
              <label style={{ display: 'flex', alignItems: 'center', gap: space.xs, fontSize: text.sec, color: colors.textSec, whiteSpace: 'nowrap' }} title="Para cargar a propósito un artículo sin precio en la lista (elegís el precio al agregarlo)">
                <input type="checkbox" checked={fueraDeLista} onChange={e => setFueraDeLista(e.target.checked)} />
                Incluir artículos fuera de {lista}
              </label>
            )}
          </div>
          <div style={st.meta}>
            <span>
              <b style={{ color: colors.text }}>{filas.length}</b>
              {total > filas.length ? ` de ${total} (afiná el filtro para ver el resto)` : ' artículos'}
              {cargando ? ' · buscando…' : ''}
            </span>
            <span style={{ color: colors.textMeta }}>{simple ? 'click elige el artículo' : 'click selecciona · Shift+click rango · tilde de cabecera = todo lo filtrado'}</span>
          </div>
          <div style={st.tscroll}>
            <table style={st.tabla}>
              <colgroup>
                {!simple && <col style={{ width: 32 }} />}{conFoto && <col style={{ width: 56 }} />}<col style={{ width: 84 }} />{conFoto && <col style={{ width: 118 }} />}<col />
                <col style={{ width: 100 }} /><col style={{ width: 128 }} /><col style={{ width: 118 }} />{conPrecio && <col style={{ width: 112 }} />}
              </colgroup>
              <thead>
                <tr>
                  {!simple && <th style={st.th}><input type="checkbox" checked={todosFiltrados} ref={el => { if (el) el.indeterminate = !todosFiltrados && algunoFiltrado }} onChange={toggleTodos} title="Seleccionar todo lo filtrado" /></th>}
                  {conFoto && <th style={st.th}>Foto</th>}
                  <th style={st.th}>Código</th>
                  {conFoto && <th style={st.th}>EAN</th>}
                  <th style={st.th}>Descripción</th>
                  <th style={st.th}>Rubro</th>
                  <th style={st.th}>Subrubro</th>
                  <th style={st.th}>Proveedor</th>
                  {conPrecio && <th style={{ ...st.th, textAlign: 'right' }}>Precio</th>}
                </tr>
              </thead>
              <tbody>
                {filas.map((a, ix) => {
                  const on = sel.has(a.id)
                  const p = precios ? precios[a.id] : null
                  return (
                    <tr key={a.id}
                      onClick={e => clickFila(ix, e)}
                      style={{ ...st.tr, ...(on ? { background: colors.linkBg } : {}) }}>
                      {!simple && <td style={st.td}><input type="checkbox" readOnly checked={on} /></td>}
                      {conFoto && (
                        <td style={{ ...st.td, padding: '2px 8px' }}>
                          {a.foto_v ? (
                            <img src={`${fotoArticuloUrl(a.codigo_alfa)}?v=${a.foto_v}`} alt="" loading="lazy" style={st.thumb}
                              onMouseEnter={e => { const r = e.currentTarget.getBoundingClientRect(); setPrevia({ src: e.currentTarget.src, x: r.right + 8, y: r.top }) }}
                              onMouseLeave={() => setPrevia(null)} />
                          ) : <span style={st.sinFoto}>sin foto</span>}
                        </td>
                      )}
                      <td style={{ ...st.td, ...sx.mono, fontSize: text.sec }}>{a.codigo_alfa}</td>
                      {conFoto && <td style={{ ...st.td, ...sx.mono, fontSize: text.sec, color: colors.textSec }}>{a.codigo_ean}</td>}
                      <td style={{ ...st.td, ...st.tdDesc }}>{a.descripcion}</td>
                      <td style={{ ...st.td, ...st.dim }}>{a.rubro}</td>
                      <td style={{ ...st.td, ...st.dim }}>{a.subrubro}</td>
                      <td style={{ ...st.td, ...st.dim }}>{a.nomprov}</td>
                      {conPrecio && (
                        <td style={{ ...st.td, textAlign: 'right' }}>
                          {p != null ? <span style={sx.mono}>{fmt(p)}</span> : <span style={st.sinPrecio}>elegir precio</span>}
                        </td>
                      )}
                    </tr>
                  )
                })}
                {filas.length === 0 && !cargando && (
                  <tr><td colSpan={9} style={{ ...st.td, textAlign: 'center', color: colors.textMeta, height: 60 }}>Sin resultados con estos filtros.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {!simple && (
        <div style={st.panel}>
          <h4 style={sx.cardTitle}>Seleccionados{sel.size ? ` · ${resumenSel}` : ''}</h4>
          {sel.size === 0 ? (
            <div style={st.vacio}>Nada seleccionado todavía.</div>
          ) : (
            <div style={{ maxHeight: 330, overflowY: 'auto' }}>
              {[...sel.values()].map(({ articulo, cantidad }) => (
                <div key={articulo.id} style={sinCantidad ? { ...st.selRow, gridTemplateColumns: '1fr 20px' } : st.selRow}>
                  <span style={st.selDesc} title={articulo.descripcion}>
                    {articulo.descripcion}
                    <br /><span style={{ ...sx.mono, fontSize: text.meta, color: colors.textSec }}>{articulo.codigo_alfa}</span>
                  </span>
                  {!sinCantidad && <input
                    className="celda-grilla"
                    style={st.cant}
                    type="text" inputMode="numeric"
                    value={cantidad}
                    onClick={e => e.stopPropagation()}
                    onChange={e => setCantidad(articulo.id, e.target.value.replace(/\D/g, ''))}
                    title="Cantidad"
                  />}
                  <button style={st.quitar} title="Quitar" onClick={() => toggle(articulo)}>✕</button>
                </div>
              ))}
            </div>
          )}
          {sel.size > 0 && conPrecio && (
            <div style={st.totline}>
              <b>Total estimado</b>
              <b style={sx.mono}>{fmt(totalSel)}{haySinPrecio ? ' *' : ''}</b>
            </div>
          )}
          {haySinPrecio && sel.size > 0 && conPrecio && (
            <div style={st.nota}>* hay artículos sin precio en la lista — al agregarlos elegís el precio.</div>
          )}
          <div style={{ display: 'flex', gap: space.sm, marginTop: space.lg }}>
            <Boton variante="ghost" onClick={onClose} style={{ flex: 1 }}>Cancelar</Boton>
            <Boton variante="primary" onClick={confirmar} disabled={sel.size === 0} style={{ flex: 2 }}>
              {textoConfirmar || '➕ Agregar'}{sel.size ? ` (${sel.size})` : ''}
            </Boton>
          </div>
        </div>
        )}
      </div>
      {previa && (
        <img src={previa.src} alt="" style={{ ...st.previa, left: Math.min(previa.x, window.innerWidth - 272), top: Math.max(8, Math.min(previa.y - 100, window.innerHeight - 272)) }} />
      )}
    </Modal>
  )
}

// Pantalla de celular: el modal pasa a tarjetas apiladas.
function useEsAngosto(max = 700) {
  const [angosto, setAngosto] = useState(() => window.matchMedia(`(max-width: ${max}px)`).matches)
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${max}px)`)
    const cambio = (e) => setAngosto(e.matches)
    mq.addEventListener('change', cambio)
    return () => mq.removeEventListener('change', cambio)
  }, [max])
  return angosto
}

// Estilos de la versión celular (targets táctiles ≥ 40px, 16px en los
// inputs para que Safari no haga zoom al enfocar).
const stc = {
  input: { flex: 1, minWidth: 0, boxSizing: 'border-box', height: 42, border: `1px solid ${colors.border}`, borderRadius: 10, padding: '0 12px', fontSize: 16, color: colors.text, fontFamily: 'inherit', outline: 'none' },
  select: { width: '100%', boxSizing: 'border-box', height: 40, border: `1px solid ${colors.border}`, borderRadius: 10, padding: '0 8px', fontSize: 16, color: colors.text, fontFamily: 'inherit', background: colors.bg },
  filtros: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: space.sm, marginTop: space.sm },
  btnFiltros: { flexShrink: 0, height: 42, padding: '0 12px', border: `1px solid ${colors.border}`, borderRadius: 10, background: colors.bg, color: colors.textSec, fontSize: text.body, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer' },
  linkBtn: { border: 'none', background: 'none', color: colors.link, fontSize: text.sec, fontWeight: 600, cursor: 'pointer', padding: '6px 0', fontFamily: 'inherit' },
  card: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', border: `1px solid ${colors.border}`, borderRadius: 12, background: colors.bg, cursor: 'pointer', WebkitTapHighlightColor: 'rgba(15,23,42,0.06)' },
  check: { width: 22, height: 22, flexShrink: 0, borderRadius: 6, border: `2px solid ${colors.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: '#fff', boxSizing: 'border-box' },
  checkOn: { background: colors.primary, borderColor: colors.primary },
  desc: { fontSize: 14, fontWeight: 600, color: colors.text, lineHeight: 1.3, overflowWrap: 'anywhere' },
  sub: { fontSize: text.sec, color: colors.textSec, marginTop: 2 },
  cant: { width: 52, height: 40, flexShrink: 0, boxSizing: 'border-box', border: `1px solid ${colors.border}`, borderRadius: 10, textAlign: 'center', fontSize: 16, background: colors.bg, ...sx.mono },
  barra: { position: 'sticky', bottom: '-1.25rem', margin: '12px -1.25rem -1.25rem', padding: '12px 1.25rem', background: colors.bg, borderTop: `1px solid ${colors.border}` },
  btnCancelar: { flex: 1, height: 46, border: `1px solid ${colors.border}`, borderRadius: 12, background: colors.bg, color: colors.textSec, fontSize: 15, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer' },
  btnAgregar: { flex: 2, height: 46, border: 'none', borderRadius: 12, background: colors.primary, color: '#fff', fontSize: 15, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer' },
}

const st = {
  layout: { display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 260px', gap: space.lg, alignItems: 'start' },
  layoutSimple: { display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: space.lg, alignItems: 'start' },
  filtros: { display: 'grid', gridTemplateColumns: 'minmax(160px, 1.3fr) 1fr 1fr 1fr auto', gap: space.sm, marginBottom: space.sm },
  input: { boxSizing: 'border-box', height: 28, border: `1px solid ${colors.textPlh}`, borderRadius: radius.xs, padding: '0 8px', fontSize: text.body, color: colors.text, fontFamily: 'inherit', width: '100%' },
  filtros2: { display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 190px))', gap: space.sm, marginBottom: space.sm },
  meta: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: text.meta, color: colors.textSec, marginBottom: space.xs },
  tscroll: { maxHeight: 430, overflow: 'auto', border: `1px solid ${colors.border}`, borderRadius: radius.sm },
  tabla: { borderCollapse: 'collapse', width: '100%', fontSize: text.body, tableLayout: 'fixed' },
  th: { position: 'sticky', top: 0, zIndex: 2, textAlign: 'left', fontSize: text.meta, letterSpacing: '0.05em', textTransform: 'uppercase', color: colors.textSec, background: colors.bgSoft, borderBottom: `1px solid ${colors.border}`, padding: '5px 8px', fontWeight: 600, whiteSpace: 'nowrap' },
  tr: { cursor: 'pointer' },
  thumb: { width: 40, height: 40, objectFit: 'contain', display: 'block', background: '#fff', borderRadius: radius.xs },
  sinFoto: { fontSize: text.meta, color: colors.textPlh },
  previa: { position: 'fixed', width: 260, height: 260, objectFit: 'contain', background: '#fff', border: `1px solid ${colors.border}`, borderRadius: radius.md, boxShadow: '0 12px 32px rgba(15,23,42,.25)', zIndex: 2000, pointerEvents: 'none' },
  td: { borderBottom: `1px solid ${colors.borderSoft}`, padding: '3px 8px', height: 30, verticalAlign: 'middle', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  // La descripción NUNCA se corta: es lo que el operador está buscando.
  tdDesc: { whiteSpace: 'normal', overflow: 'visible', textOverflow: 'clip', lineHeight: 1.3, padding: '5px 8px' },
  dim: { color: colors.textSec, fontSize: text.sec },
  sinPrecio: { fontSize: text.meta, color: colors.t80Text, fontWeight: 600 },
  panel: { border: `1px solid ${colors.border}`, borderRadius: radius.md, padding: space.lg, background: colors.bgSoft, position: 'sticky', top: 0 },
  vacio: { color: colors.textMeta, fontSize: text.sec, textAlign: 'center', padding: `${space.xl}px 0` },
  avisos: { background: colors.warnBg, border: `1px solid ${colors.warnBorder}`, color: colors.warn, borderRadius: radius.sm, padding: '8px 12px', fontSize: text.sec, marginBottom: space.sm, lineHeight: 1.5 },
  selRow: { display: 'grid', gridTemplateColumns: '1fr 46px 20px', gap: 6, alignItems: 'center', padding: '4px 0', borderBottom: `1px solid ${colors.border}` },
  selDesc: { fontSize: text.sec, minWidth: 0, lineHeight: 1.3, overflowWrap: 'anywhere' },
  cant: { boxSizing: 'border-box', height: 24, border: `1px solid ${colors.textPlh}`, borderRadius: radius.xs, padding: '0 5px', fontSize: text.sec, textAlign: 'right', ...sx.mono },
  quitar: { border: 'none', background: 'none', color: colors.textSec, cursor: 'pointer', fontSize: text.body, padding: 0 },
  totline: { display: 'flex', justifyContent: 'space-between', fontSize: text.body, marginTop: space.md, paddingTop: space.md, borderTop: `2px solid ${colors.borderStrong}` },
  nota: { fontSize: text.meta, color: colors.textSec, marginTop: space.xs, lineHeight: 1.4 },
}
